import { useState, type FormEvent } from 'react';
import { useAuth } from '../auth/AuthContext';
import { Icon } from '../components/Icon';
import { PoweredByBadge } from '../components/PoweredByBadge';
import { ThreeThirtyLogo } from '../components/ThreeThirtyLogo';

export function SignIn() {
  const { signIn } = useAuth();
  const [tenantSlug, setTenantSlug] = useState('abny');
  const [email, setEmail] = useState('owner@abny.in');
  const [password, setPassword] = useState('ChangeMe!2026');
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(tenantSlug.trim(), email.trim(), password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed.');
    } finally {
      setBusy(false);
    }
  }

  function setDemoUser(demoEmail: string, demoRole: string) {
    setEmail(demoEmail);
    setPassword('ChangeMe!2026');
    setTenantSlug('abny');
    setError(null);
  }

  return (
    <div className="signin-page-wrapper">
      {/* =========================================================================
          1. DESKTOP CENTERED DUAL-CARD LAYOUT (Matching Screenshot 1)
          ========================================================================= */}
      <div className="desktop-signin-stage">
        <div className="dual-card-wrapper">
          {/* Left Themed Brand Feature Card */}
          <div className="brand-feature-card">
            <div className="feature-card-content">
              <div className="brand-symbol-circle">
                <ThreeThirtyLogo size={24} variant="badge" />
              </div>

              <div className="feature-hero-text">
                <span className="hero-eyebrow">We Are</span>
                <h1 className="hero-title">Three Thirty</h1>
                <span className="hero-badge">ENTERPRISE</span>
              </div>

              <p className="feature-description">
                Leadership systems, workforce intelligence, and automated payroll governance.
              </p>

              <div className="feature-actions">
                <a
                  href="https://threethirtyllc.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="feature-learn-btn"
                >
                  <span>Learn More</span>
                  <Icon name="arrowRight" size="xs" />
                </a>
              </div>
            </div>

            {/* Aesthetic Wave / Glow Circles in Background */}
            <div className="feature-wave-glow" />
            <div className="feature-wave-glow-alt" />

            {/* Pagination Indicators */}
            <div className="feature-pagination">
              <span className="dot active" />
              <span className="dot" />
              <span className="dot" />
            </div>
          </div>

          {/* Right Clean Form Card */}
          <div className="form-portal-card">
            <div className="form-card-header">
              <h2 className="form-card-greeting">Hello!</h2>
              <p className="form-card-sub">
                Sign in to access your enterprise workforce portal.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="signin-form-stack">
              <div className="custom-form-field">
                <label htmlFor="tenant-desk">ORGANISATION</label>
                <input
                  id="tenant-desk"
                  value={tenantSlug}
                  onChange={(e) => setTenantSlug(e.target.value)}
                  placeholder="abny or threethirty"
                  autoComplete="organization"
                  required
                />
              </div>

              <div className="custom-form-field">
                <label htmlFor="email-desk">EMAIL ADDRESS</label>
                <input
                  id="email-desk"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@threethirtyllc.com"
                  autoComplete="username"
                  required
                />
              </div>

              <div className="custom-form-field">
                <div className="field-split-label">
                  <label htmlFor="password-desk">PASSWORD</label>
                  <span className="forgot-hint">DEFAULT: ChangeMe!2026</span>
                </div>
                <input
                  id="password-desk"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  autoComplete="current-password"
                  required
                />
              </div>

              {error && <div className="banner error">{error}</div>}

              <button
                className="desktop-login-btn"
                type="submit"
                disabled={busy}
              >
                <span>{busy ? 'Authenticating…' : 'LOGIN'}</span>
                {!busy && <Icon name="arrowRight" size="xs" />}
              </button>
            </form>

            <div className="desktop-demo-section">
              <span className="demo-section-label">QUICK PERSONA SWITCHER</span>
              <div className="demo-pills-row">
                <button type="button" className="demo-pill" onClick={() => setDemoUser('owner@abny.in', 'Owner')}>
                  <Icon name="award" size="xs" />
                  <span>Owner</span>
                </button>
                <button type="button" className="demo-pill" onClick={() => setDemoUser('finance@abny.test', 'Finance')}>
                  <Icon name="bank" size="xs" />
                  <span>Finance</span>
                </button>
                <button type="button" className="demo-pill" onClick={() => setDemoUser('payroll@abny.test', 'Payroll')}>
                  <Icon name="cash" size="xs" />
                  <span>Payroll</span>
                </button>
                <button type="button" className="demo-pill" onClick={() => setDemoUser('hr@abny.test', 'HR')}>
                  <Icon name="users" size="xs" />
                  <span>HR</span>
                </button>
                <button type="button" className="demo-pill" onClick={() => setDemoUser('asha@abny.test', 'Manager')}>
                  <Icon name="userCheck" size="xs" />
                  <span>Manager</span>
                </button>
                <button type="button" className="demo-pill" onClick={() => setDemoUser('rohit@abny.test', 'Employee')}>
                  <Icon name="smartphone" size="xs" />
                  <span>Staff</span>
                </button>
              </div>
            </div>

            <div className="desktop-card-footer">
              <span>THREE THIRTY LLC · Birmingham, AL</span>
            </div>
          </div>
        </div>
      </div>

      {/* =========================================================================
          2. MOBILE NATIVE CURVED APP LAYOUT (Matching Screenshot 2)
          ========================================================================= */}
      <div className="mobile-signin-stage">
        <div className="mobile-app-canvas">
          {/* Curved Dark Themed Top Header */}
          <div className="mobile-header-curve">
            <div className="mobile-brand-row">
              <div className="mobile-logo-circle">
                <ThreeThirtyLogo size={22} variant="badge" />
              </div>
              <div className="mobile-brand-title">
                <span>The</span>
                <strong>Three Thirty</strong>
                <span className="mobile-subtag">Company</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="mobile-form-box">
              <div className="mobile-input-group">
                <label>Organisation</label>
                <input
                  value={tenantSlug}
                  onChange={(e) => setTenantSlug(e.target.value)}
                  placeholder="abny"
                  required
                />
              </div>

              <div className="mobile-input-group">
                <label>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@threethirtyllc.com"
                  required
                />
              </div>

              <div className="mobile-input-group">
                <label>Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  required
                />
              </div>

              <div className="mobile-options-row">
                <label className="mobile-remember-label">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span>Remember me</span>
                </label>
                <a href="https://threethirtyllc.com/" target="_blank" rel="noopener noreferrer" className="mobile-forgot-link">
                  Support?
                </a>
              </div>

              {error && <div className="banner error" style={{ margin: '8px 0' }}>{error}</div>}

              <button
                type="submit"
                className="mobile-submit-white-btn"
                disabled={busy}
              >
                {busy ? 'Signing In…' : 'Sign in'}
              </button>
            </form>
          </div>

          {/* Bottom Curved Wave Section */}
          <div className="mobile-bottom-section">
            <span className="mobile-demo-label">Quick Demo Role Login</span>
            <div className="mobile-demo-grid">
              <button type="button" className="mobile-pill-btn" onClick={() => setDemoUser('owner@abny.in', 'Owner')}>
                <Icon name="award" size="xs" />
                <span>Owner</span>
              </button>
              <button type="button" className="mobile-pill-btn" onClick={() => setDemoUser('finance@abny.test', 'Finance')}>
                <Icon name="landmark" size="xs" />
                <span>Finance</span>
              </button>
              <button type="button" className="mobile-pill-btn" onClick={() => setDemoUser('payroll@abny.test', 'Payroll')}>
                <Icon name="wallet" size="xs" />
                <span>Payroll</span>
              </button>
              <button type="button" className="mobile-pill-btn" onClick={() => setDemoUser('hr@abny.test', 'HR')}>
                <Icon name="users" size="xs" />
                <span>HR Admin</span>
              </button>
              <button type="button" className="mobile-pill-btn" onClick={() => setDemoUser('asha@abny.test', 'Manager')}>
                <Icon name="userCheck" size="xs" />
                <span>Manager</span>
              </button>
              <button type="button" className="mobile-pill-btn" onClick={() => setDemoUser('rohit@abny.test', 'Employee')}>
                <Icon name="user" size="xs" />
                <span>Staff</span>
              </button>
            </div>

            {/* Centered Modern Powered By ABNY Web Badge for Mobile */}
            <PoweredByBadge position="center-inline" />

            <div className="mobile-footer-link">
              <a href="https://threethirtyllc.com/" target="_blank" rel="noopener noreferrer">
                THREE THIRTY LLC · threethirtyllc.com
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Centered Modern Powered By ABNY Web Badge for Desktop */}
      <PoweredByBadge position="bottom-center" className="desktop-only-powered-by" />
    </div>
  );
}



